import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import { GET_ALL_GISTS, GET_COMMENT } from "../../services/graphql/queriesMutations";
import styles from "./CommentExplorerLabPage.module.css";

type LabUser = {
  id?: number;
  name?: string | null;
  image?: string | null;
};

type LabComment = {
  id: number;
  comment?: string | null;
  parentId?: number | null;
  userId?: number | null;
  gistId?: number | null;
  editId?: number | null;
  isRemoved?: boolean | null;
  removedAt?: string | null;
  removedReason?: string | null;
  user?: LabUser | null;
  createdAt?: string | null;
  replies?: LabComment[] | null;
};

type LabEdit = {
  id: number;
  body?: string | null;
  createdAt?: string | null;
  comments?: LabComment[] | null;
};

type LabVersion = {
  id: number;
  point?: string | null;
  createdAt?: string | null;
  edits?: LabEdit[] | null;
  user?: LabUser | null;
};

type LabGist = {
  id: number;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
  status?: string | null;
  createdAt?: string | null;
  versions?: LabVersion[] | null;
};

type GistsQuery = {
  gists: LabGist[];
};

type CommentsQuery = {
  comments: LabComment[];
};

type CommentNode = {
  comment: LabComment;
  children: CommentNode[];
  depth: number;
  totalDescendants: number;
};

type PositionedCommentNode = CommentNode & {
  x: number;
  y: number;
};

type PositionedLink = {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  hot: boolean;
};

type CommentLayout = {
  nodes: PositionedCommentNode[];
  links: PositionedLink[];
  canvasWidth: number;
  canvasHeight: number;
};

const CARD_WIDTH = 310;
const CARD_HEIGHT = 178;
const NUCLEUS_X = 70;
const NUCLEUS_Y = 90;
const FIRST_COMMENT_X = 560;
const COLUMN_GAP = 455;
const ROW_GAP = 234;
const TOP_ALIGNED_Y = 80;
const MIN_CANVAS_WIDTH = 1600;
const MIN_CANVAS_HEIGHT = 940;

function stripHtml(input?: string | null): string {
  if (!input) return "";
  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    const doc = new window.DOMParser().parseFromString(input, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  return input.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function excerpt(input?: string | null, limit = 210): string {
  const text = stripHtml(input);
  if (!text) return "No text available.";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}…`;
}

function formatDate(date?: string | null): string {
  if (!date) return "unknown date";
  const parsed = new Date(date).getTime();
  if (Number.isNaN(parsed)) return "unknown date";
  return new Date(parsed).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getLatestVersion(gist: LabGist | null): LabVersion | null {
  if (!gist?.versions?.length) return null;
  return gist.versions
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getLatestEdit(gist: LabGist | null): LabEdit | null {
  const version = getLatestVersion(gist);
  if (!version?.edits?.length) return null;
  return version.edits
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
}

function getCommentCountFromGist(gist: LabGist): number {
  const seen: { [id: number]: true } = {};
  let count = 0;
  (gist.versions || []).forEach((version) => {
    (version.edits || []).forEach((edit) => {
      (edit.comments || []).forEach((comment) => {
        if (!seen[comment.id]) {
          seen[comment.id] = true;
          count += 1;
        }
      });
    });
  });
  return count;
}

function sortGists(gists: LabGist[]): LabGist[] {
  return gists.slice().sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    if (aTime !== bTime) return bTime - aTime;
    return a.title.localeCompare(b.title);
  });
}

function collectComments(rawComments: LabComment[]): LabComment[] {
  const byId: { [id: number]: LabComment } = {};

  const visit = (comment: LabComment) => {
    if (!comment || typeof comment.id !== "number") return;
    if (!byId[comment.id]) {
      byId[comment.id] = { ...comment, replies: [] };
    } else {
      byId[comment.id] = { ...byId[comment.id], ...comment, replies: byId[comment.id].replies || [] };
    }
    (comment.replies || []).forEach((reply) => visit(reply));
  };

  rawComments.forEach((comment) => visit(comment));

  const result: LabComment[] = [];
  Object.keys(byId).forEach((key) => {
    result.push(byId[Number(key)]);
  });

  return result.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
}

function buildCommentTree(rawComments: LabComment[]): CommentNode[] {
  const comments = collectComments(rawComments);
  const byId: { [id: number]: LabComment } = {};
  comments.forEach((comment) => {
    byId[comment.id] = comment;
  });

  const buckets: { [parentKey: string]: LabComment[] } = {};
  comments.forEach((comment) => {
    const hasKnownParent = typeof comment.parentId === "number" && Boolean(byId[comment.parentId]);
    const key = hasKnownParent ? String(comment.parentId) : "root";
    buckets[key] = buckets[key] || [];
    buckets[key].push(comment);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key] = buckets[key].slice().sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
  });

  const buildNode = (comment: LabComment, depth: number, path: { [id: number]: boolean }): CommentNode => {
    if (path[comment.id]) {
      return { comment, children: [], depth, totalDescendants: 0 };
    }

    const nextPath = { ...path, [comment.id]: true };
    const children = (buckets[String(comment.id)] || []).map((child) => buildNode(child, depth + 1, nextPath));
    const totalDescendants = children.reduce((sum, child) => sum + 1 + child.totalDescendants, 0);
    return { comment, children, depth, totalDescendants };
  };

  return (buckets.root || []).map((comment) => buildNode(comment, 0, {}));
}

function layoutCommentTree(roots: CommentNode[]): CommentLayout {
  const nodes: PositionedCommentNode[] = [];
  const links: PositionedLink[] = [];
  const nextYByDepth: { [depth: number]: number } = {};

  const takeNextY = (depth: number): number => {
    const y = nextYByDepth[depth] ?? TOP_ALIGNED_Y;
    nextYByDepth[depth] = y + ROW_GAP;
    return y;
  };

  const layoutNode = (node: CommentNode): PositionedCommentNode => {
    const positioned: PositionedCommentNode = {
      ...node,
      x: FIRST_COMMENT_X + node.depth * COLUMN_GAP,
      y: takeNextY(node.depth),
    };
    nodes.push(positioned);

    node.children.forEach((child) => {
      const childPosition = layoutNode(child);
      links.push({
        id: `${positioned.comment.id}-${child.comment.id}`,
        fromX: positioned.x + CARD_WIDTH,
        fromY: positioned.y + Math.round(CARD_HEIGHT * 0.46),
        toX: childPosition.x,
        toY: childPosition.y + Math.round(CARD_HEIGHT * 0.46),
        hot: positioned.depth === 0,
      });
    });

    return positioned;
  };

  roots.forEach((root) => {
    const rootPosition = layoutNode(root);
    links.push({
      id: `gist-${rootPosition.comment.id}`,
      fromX: NUCLEUS_X + 350,
      fromY: NUCLEUS_Y + 95,
      toX: rootPosition.x,
      toY: rootPosition.y + Math.round(CARD_HEIGHT * 0.46),
      hot: true,
    });
  });

  const maxX = nodes.length ? Math.max.apply(null, nodes.map((node) => node.x)) : FIRST_COMMENT_X;
  const maxY = nodes.length ? Math.max.apply(null, nodes.map((node) => node.y)) : 180;

  return {
    nodes,
    links,
    canvasWidth: Math.max(MIN_CANVAS_WIDTH, maxX + CARD_WIDTH + 260),
    canvasHeight: Math.max(MIN_CANVAS_HEIGHT, maxY + CARD_HEIGHT + 190),
  };
}

function getCommentTitle(comment: LabComment): string {
  const text = stripHtml(comment.isRemoved ? "Comment removed" : comment.comment);
  if (!text) return comment.isRemoved ? "Removed comment" : "Untitled comment";
  if (text.length <= 62) return text;
  return `${text.slice(0, 62).trim()}…`;
}

function getCommentText(comment: LabComment): string {
  if (comment.isRemoved) return comment.removedReason || "This comment was removed, but its children remain visible to preserve the thread shape.";
  return stripHtml(comment.comment) || "No comment text available.";
}

function getAuthorName(comment: LabComment): string {
  return comment.user?.name || `User ${comment.userId || "unknown"}`;
}

function getDepthLabel(depth: number): string {
  if (depth === 0) return "Top-level comment";
  if (depth === 1) return "Reply · level 1";
  return `Reply · level ${depth}`;
}

function getToneClass(depth: number, isRemoved?: boolean | null): string {
  if (isRemoved) return styles.removedCommentCard;
  if (depth === 0) return styles.rootCommentCard;
  if (depth === 1) return styles.replyCommentCard;
  if (depth === 2) return styles.deepReplyCommentCard;
  return styles.deeperReplyCommentCard;
}

function getRootCommentCount(nodes: CommentNode[]): number {
  return nodes.length;
}

function getTotalNodeCount(nodes: CommentNode[]): number {
  return nodes.reduce((sum, node) => sum + 1 + node.totalDescendants, 0);
}

const CommentExplorerLabPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedGistId = useMemo(() => {
    const raw = searchParams.get("gistId");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  const { data: gistData, loading: gistsLoading, error: gistsError } = useQuery<GistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const [selectedGistId, setSelectedGistId] = useState<number | null>(null);
  const [focusedCommentId, setFocusedCommentId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  const gists = useMemo(() => sortGists(gistData?.gists || []), [gistData]);

  useEffect(() => {
    if (!gists.length) return;

    if (requestedGistId && gists.some((gist) => gist.id === requestedGistId)) {
      if (selectedGistId !== requestedGistId) setSelectedGistId(requestedGistId);
      return;
    }

    if (selectedGistId && gists.some((gist) => gist.id === selectedGistId)) return;

    const firstWithComments = gists.find((gist) => getCommentCountFromGist(gist) > 0);
    setSelectedGistId((firstWithComments || gists[0]).id);
  }, [gists, requestedGistId, selectedGistId]);

  const { data: commentsData, loading: commentsLoading, error: commentsError } = useQuery<CommentsQuery>(GET_COMMENT, {
    variables: { gistId: selectedGistId, editId: null },
    skip: !selectedGistId,
    fetchPolicy: "cache-and-network",
  });

  const selectedGist = useMemo(() => {
    if (!selectedGistId) return null;
    return gists.find((gist) => gist.id === selectedGistId) || null;
  }, [gists, selectedGistId]);

  const latestVersion = getLatestVersion(selectedGist);
  const latestEdit = getLatestEdit(selectedGist);
  const roots = useMemo(() => buildCommentTree(commentsData?.comments || []), [commentsData]);
  const layout = useMemo(() => layoutCommentTree(roots), [roots]);
  const focusedNode = focusedCommentId ? layout.nodes.find((node) => node.comment.id === focusedCommentId) || null : null;

  useEffect(() => {
    setFocusedCommentId(null);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, top: 0 });
    }
  }, [selectedGistId]);

  const handleGistChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const parsed = Number(event.target.value);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setSelectedGistId(parsed);
    setSearchParams({ gistId: String(parsed) });
  };

  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, select, a, .comment-sheet-lab-card")) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startLeft: scrollEl.scrollLeft,
      startTop: scrollEl.scrollTop,
    };
    setIsDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const state = dragStateRef.current;
      scrollEl.scrollLeft = state.startLeft - (event.clientX - state.startX);
      scrollEl.scrollTop = state.startTop - (event.clientY - state.startY);
    };

    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const resetSheet = () => {
    scrollRef.current?.scrollTo({ left: 0, top: 0, behavior: "smooth" });
  };

const loading = gistsLoading || commentsLoading;
  const error = gistsError || commentsError;
  const totalComments = getTotalNodeCount(roots);
  const rootComments = getRootCommentCount(roots);
  const productionGistLink = selectedGist?.subjectId
    ? `/subject/Subject/${selectedGist.subjectId}?gistId=${selectedGist.id}`
    : selectedGist
      ? `/gist/${encodeURIComponent(selectedGist.title)}/${selectedGist.id}`
      : "/";

  return (
    <main className={styles.pageShell}>
      <section className={styles.labFrame}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Sandbox · removable page · read-only comments</p>
            <h1>Comment Lab Infinite Sheet</h1>
            <p className={styles.intro}>
              A selected Gist stays open at the top. Its comments appear below as an unlimited sheet with parent → child links.
            </p>
          </div>
          <div className={styles.headerPills}>
            <span>{rootComments} root comments</span>
            <span>{totalComments} comments total</span>
            <span>read-only</span>
          </div>
        </header>

        <section className={styles.gistPanel}>
          <div className={styles.gistToolbar}>
            <label className={styles.gistSelectLabel}>
              <span>Selected Gist</span>
              <select value={selectedGistId || ""} onChange={handleGistChange}>
                {gists.map((gist) => (
                  <option key={gist.id} value={gist.id}>
                    #{gist.id} · {gist.title} · {getCommentCountFromGist(gist)} comments
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.gistActions}>
              {selectedGist && (
                <Link to={productionGistLink} className={styles.actionButton}>
                  Open production Gist
                </Link>
              )}
              <button type="button" className={styles.actionButton} onClick={resetSheet}>
                Reset comments sheet
              </button>
            </div>
          </div>

          <article className={styles.openedGistCard}>
            <p className={styles.cardKicker}>Read-only Gist · #{selectedGist?.id || "—"} · {formatDate(selectedGist?.createdAt)}</p>
            <h2>{selectedGist?.title || "No Gist selected"}</h2>
            <p className={styles.openedPoint}>{stripHtml(latestVersion?.point) || "No summary available."}</p>
            <div className={styles.openedBody}>{stripHtml(latestEdit?.body) || "No body available in the latest edit."}</div>
          </article>
        </section>

        <section className={styles.sheetPanel}>
          <div className={styles.sheetToolbar}>
            <div>
              <span className={styles.pathLabel}>Comment sheet</span>
              <strong>{selectedGist?.title || "No Gist selected"}</strong>
              <em>Click a comment to expand it to a 70% reading card. Click the large card again to return to the sheet.</em>
            </div>
            {loading && <span className={styles.statePill}>Loading…</span>}
            {error && <span className={styles.errorPill}>Could not load: {error.message}</span>}
          </div>

          <div
            ref={scrollRef}
            className={`${styles.sheetScroll} ${isDragging ? styles.sheetScrollDragging : ""}`}
            onMouseDown={handleCanvasMouseDown}
          >
            <div className={styles.sheetCanvas} style={{ width: layout.canvasWidth, height: layout.canvasHeight }}>
              <svg className={styles.links} width={layout.canvasWidth} height={layout.canvasHeight} viewBox={`0 0 ${layout.canvasWidth} ${layout.canvasHeight}`}>
                {layout.links.map((link) => {
                  const midX = Math.round((link.fromX + link.toX) / 2);
                  return (
                    <path
                      key={link.id}
                      className={`${styles.linkPath} ${link.hot ? styles.linkPathHot : ""}`}
                      d={`M ${link.fromX} ${link.fromY} C ${midX} ${link.fromY}, ${midX} ${link.toY}, ${link.toX} ${link.toY}`}
                    />
                  );
                })}
              </svg>

              <article className={`${styles.gistNucleus} comment-sheet-lab-card`} style={{ left: NUCLEUS_X, top: NUCLEUS_Y }}>
                <span>Gist nucleus</span>
                <h2>{selectedGist?.title || "Selected Gist"}</h2>
                <p>This card anchors the comment tree. First-level cards are direct comments under this Gist.</p>
                <div className={styles.cardChips}>
                  <span>{rootComments} direct comments</span>
                  <span>{totalComments} visible total</span>
                </div>
              </article>

              {totalComments === 0 && !loading && (
                <div className={styles.emptySheet}>
                  <h2>No comments found for this Gist.</h2>
                  <p>Choose another Gist from the selector, or add test comments elsewhere and return to this read-only lab.</p>
                </div>
              )}

              {layout.nodes.map((node) => {
                const comment = node.comment;
                return (
                  <article
                    key={comment.id}
                    className={`${styles.commentCard} ${getToneClass(node.depth, comment.isRemoved)} comment-sheet-lab-card`}
                    style={{ left: node.x, top: node.y }}
                    onClick={() => setFocusedCommentId(comment.id)}
                  >
                    <div className={styles.cardKicker}>{getDepthLabel(node.depth)} · #{comment.id} · {formatDate(comment.createdAt)}</div>
                    <h3>{getCommentTitle(comment)}</h3>
                    <p>{excerpt(getCommentText(comment), 185)}</p>
                    <div className={styles.cardChips}>
                      <span>{getAuthorName(comment)}</span>
                      <span>{node.children.length} direct replies</span>
                      <span>{node.totalDescendants} total replies</span>
                    </div>
                  </article>
                );
              })}

              {focusedNode && (
                <section className={styles.focusLayer} onClick={() => setFocusedCommentId(null)}>
                  <div className={`${styles.rope} ${styles.ropeTop}`} />
                  <div className={`${styles.rope} ${styles.ropeRight}`} />
                  <div className={`${styles.rope} ${styles.ropeBottom}`} />
                  <div className={`${styles.rope} ${styles.ropeLeft}`} />
                  <span className={`${styles.ropeLabel} ${styles.ropeLabelTop}`}>parent / Gist link</span>
                  <span className={`${styles.ropeLabel} ${styles.ropeLabelRight}`}>sibling / next branch</span>
                  <span className={`${styles.ropeLabel} ${styles.ropeLabelBottom}`}>children</span>
                  <span className={`${styles.ropeLabel} ${styles.ropeLabelLeft}`}>previous context</span>
                  <article className={styles.focusCard} onClick={() => setFocusedCommentId(null)}>
                    <p className={styles.cardKicker}>{getDepthLabel(focusedNode.depth)} · #{focusedNode.comment.id} · {formatDate(focusedNode.comment.createdAt)}</p>
                    <h2>{getCommentTitle(focusedNode.comment)}</h2>
                    <p className={styles.focusMeta}>By {getAuthorName(focusedNode.comment)} · {focusedNode.children.length} direct replies · {focusedNode.totalDescendants} total descendant replies</p>
                    <div className={styles.focusText}>{getCommentText(focusedNode.comment)}</div>
                    <p className={styles.focusHint}>Click this large comment again to collapse back to the unlimited sheet.</p>
                  </article>
                </section>
              )}
            </div>
          </div>
        </section>
      </section>
    </main>
  );
};

export default CommentExplorerLabPage;
